/**
 * Onglet Axe Nodal — Noeud Nord/Sud avec descriptions karmiques
 * Layout 3 colonnes : Sud | Flèche ÉVOLUTION | Nord
 * + Aspects détaillés aux noeuds lunaires
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import type { ChartAnalysis } from '../../types/clients.js';

type AxeData = NonNullable<ChartAnalysis['axeNodal']>;

/** Descriptions par signe pour le Noeud Nord */
const DESCRIPTIONS_NORD: Record<string, string> = {
  'Bélier': "Développer l'affirmation de soi, le courage, l'initiative personnelle",
  'Taureau': "Cultiver la stabilité, les valeurs propres, le rapport à la matière",
  'Gémeaux': "Apprendre la communication, la curiosité, les échanges multiples",
  'Cancer': "Développer la sensibilité, l'intimité, les racines émotionnelles",
  'Lion': "Exprimer sa créativité, son unicité, prendre sa place au centre",
  'Vierge': "Cultiver le discernement, le service, l'amélioration concrète",
  'Balance': "Apprendre la relation, le compromis, l'harmonie avec l'autre",
  'Scorpion': "Explorer les profondeurs, la transformation, le pouvoir partagé",
  'Sagittaire': "Développer la vision, la quête de sens, l'expansion spirituelle",
  'Capricorne': "Construire avec responsabilité, ambition, structuration",
  'Verseau': "Cultiver l'originalité, le collectif, la liberté d'être différent",
  'Poissons': "Développer la compassion, la spiritualité, le lâcher-prise",
};

/** Descriptions par signe pour le Noeud Sud */
const DESCRIPTIONS_SUD: Record<string, string> = {
  'Bélier': "Impulsivité passée, égocentrisme à dépasser",
  'Taureau': "Attachement excessif à la sécurité matérielle",
  'Gémeaux': "Dispersion passée, superficialité dans les échanges",
  'Cancer': "Dépendance émotionnelle, refuge excessif dans le passé",
  'Lion': "Ego surdimensionné, besoin de reconnaissance à transformer",
  'Vierge': "Perfectionnisme paralysant, critique excessive",
  'Balance': "Tendance passée à trop chercher l'approbation des autres",
  'Scorpion': "Attachements anciens au contrôle et aux possessions partagées",
  'Sagittaire': "Excès de certitudes, tendance au dogmatisme",
  'Capricorne': "Rigidité passée, trop de focus sur le statut social",
  'Verseau': "Détachement excessif, fuite dans le collectif",
  'Poissons': "Tendance à la fuite, confusion des limites personnelles",
};

interface Props {
  axeNodal: AxeData;
}

export function AxeNodalTab({ axeNodal }: Props) {
  const [showHelp, setShowHelp] = useState(false);
  const { nord, sud, aspectsNoeuds } = axeNodal;

  if (!nord && !sud) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
        <i className="bi bi-exclamation-circle" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
        <p>Données des noeuds lunaires non disponibles</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header — identique au PHP */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-arrow-left-right" style={{ color: colors.accent, marginRight: 8 }} />
          Axe Nodal (Axe Karmique)
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre l'Axe Nodal
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#64748b' }}
            >
              &times;
            </button>
          </div>

          <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem' }}>☊ ☋ Qu'est-ce que l'Axe Nodal ?</h4>
          <p style={{ margin: '0 0 12px' }}>Les <strong>Noeuds Lunaires</strong> sont les points d'intersection entre l'orbite de la Lune et l'écliptique. Ils forment un axe qui représente le <strong>chemin d'évolution karmique</strong>.</p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem' }}>☋ Le Noeud Sud — Le Passé</h4>
              <ul style={{ margin: '0 0 4px', paddingLeft: '1.2rem' }}>
                <li>Les <strong>acquis des vies passées</strong></li>
                <li>Les <strong>comportements automatiques</strong>, la zone de confort</li>
                <li>Les <strong>talents naturels</strong> déjà développés</li>
                <li>Les <strong>schémas à dépasser</strong></li>
              </ul>
              <p style={{ margin: 0, fontStyle: 'italic' }}>C'est notre « bagage » : utile comme fondation, mais ne doit pas devenir une prison.</p>
            </div>
            <div>
              <h4 style={{ margin: '0 0 4px', fontSize: '0.95rem' }}>☊ Le Noeud Nord — L'Avenir</h4>
              <ul style={{ margin: '0 0 4px', paddingLeft: '1.2rem' }}>
                <li>La <strong>direction d'évolution</strong> pour cette vie</li>
                <li>Les <strong>qualités à développer</strong> consciemment</li>
                <li>Le chemin qui mène à l'<strong>épanouissement</strong></li>
                <li>Ce qui semble <strong>inconfortable au début</strong> mais libérateur</li>
              </ul>
              <p style={{ margin: 0, fontStyle: 'italic' }}>C'est notre « boussole » : la direction dans laquelle l'âme cherche à grandir.</p>
            </div>
          </div>

          <h4 style={{ margin: '12px 0 4px', fontSize: '0.95rem' }}>🔗 Les Aspects aux Noeuds</h4>
          <ul style={{ margin: '0 0 4px', paddingLeft: '1.2rem' }}>
            <li><strong>Conjonction au NN</strong> : Cette planète est un guide vers l'évolution</li>
            <li><strong>Conjonction au NS</strong> : Cette planète porte des mémoires karmiques fortes</li>
            <li><strong>Carré aux noeuds</strong> : Point de tension et de choix karmique crucial</li>
            <li><strong>Trigone/Sextile</strong> : Soutien dans le parcours évolutif</li>
          </ul>

          <h4 style={{ margin: '12px 0 4px', fontSize: '0.95rem' }}>👑 Le Maître du Noeud</h4>
          <p style={{ margin: 0 }}>La planète qui <strong>gouverne le signe</strong> du Noeud Nord est particulièrement importante. Sa position indique <strong>comment et où</strong> activer la mission du Noeud Nord.</p>
        </div>
      )}

      {/* Layout 3 colonnes : Sud | Flèche | Nord — identique au PHP */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '1rem', marginBottom: '2rem' }}>
        {/* Noeud Sud */}
        {sud ? (
          <div style={{
            padding: '1.5rem', borderRadius: 12,
            background: 'linear-gradient(135deg, #faf5ff, #f3e8ff)',
            border: '2px solid #c4b5fd',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <span style={{ fontFamily: "'Noto Sans Symbols 2', sans-serif", fontSize: '2rem', color: '#7c3aed' }}>☋</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#5b21b6' }}>Noeud Sud</div>
                <div style={{ fontSize: '0.85rem', color: '#7c3aed' }}>Le Passé • Les Acquis</div>
              </div>
            </div>

            <div style={{ background: '#fff', padding: '1rem', borderRadius: 8, marginBottom: '1rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#5b21b6', marginBottom: '0.25rem' }}>{sud.signe}</div>
              <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>
                {sud.degre}
                {sud.maison ? ` • Maison ${sud.maison}` : ''}
              </div>
            </div>

            <div style={{ fontSize: '0.9rem', color: '#5b21b6', lineHeight: 1.5 }}>
              <strong>Bagages karmiques :</strong><br />
              {DESCRIPTIONS_SUD[sud.signe] ?? 'Tendances et compétences acquises dans le passé.'}
            </div>

            {sud.maitre && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e9d5ff', fontSize: '0.85rem' }}>
                <strong>Maître :</strong> {sud.maitre}
                {sud.maitreSigne && ` en ${sud.maitreSigne}`}
                {sud.maitreMaison && ` (M${sud.maitreMaison})`}
              </div>
            )}
          </div>
        ) : <div />}

        {/* Flèche ÉVOLUTION */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '2rem', color: colors.accent }}>→</div>
          <div style={{
            writingMode: 'vertical-rl', textOrientation: 'mixed',
            fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.5rem',
          }}>
            ÉVOLUTION
          </div>
        </div>

        {/* Noeud Nord */}
        {nord ? (
          <div style={{
            padding: '1.5rem', borderRadius: 12,
            background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
            border: '2px solid #6ee7b7',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <span style={{ fontFamily: "'Noto Sans Symbols 2', sans-serif", fontSize: '2rem', color: '#059669' }}>☊</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#047857' }}>Noeud Nord</div>
                <div style={{ fontSize: '0.85rem', color: '#059669' }}>L'Avenir • La Mission</div>
              </div>
            </div>

            <div style={{ background: '#fff', padding: '1rem', borderRadius: 8, marginBottom: '1rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#047857', marginBottom: '0.25rem' }}>{nord.signe}</div>
              <div style={{ fontSize: '0.9rem', color: colors.textMuted }}>
                {nord.degre}
                {nord.maison ? ` • Maison ${nord.maison}` : ''}
              </div>
            </div>

            <div style={{ fontSize: '0.9rem', color: '#047857', lineHeight: 1.5 }}>
              <strong>Direction d'évolution :</strong><br />
              {DESCRIPTIONS_NORD[nord.signe] ?? "Qualités à développer dans cette incarnation."}
            </div>

            {nord.maitre && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #a7f3d0', fontSize: '0.85rem' }}>
                <strong>Maître :</strong> {nord.maitre}
                {nord.maitreSigne && ` en ${nord.maitreSigne}`}
                {nord.maitreMaison && ` (M${nord.maitreMaison})`}
              </div>
            )}
          </div>
        ) : <div />}
      </div>

      {/* Aspects aux Noeuds Lunaires — identique au PHP */}
      {aspectsNoeuds && aspectsNoeuds.length > 0 ? (
        <div style={{ background: colors.bgPage, padding: '1.25rem', borderRadius: 8 }}>
          <h4 style={{ margin: '0 0 1rem', color: colors.text, fontSize: '1rem' }}>
            <i className="bi bi-link-45deg" style={{ color: colors.accent, marginRight: 6 }} />
            Aspects aux Noeuds Lunaires
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
            {aspectsNoeuds.map((asp, i) => {
              const isNord = asp.noeud === 'Nord';
              const bgColor = isNord ? '#ecfdf5' : '#faf5ff';
              const borderColor = isNord ? '#059669' : '#7c3aed';
              const natureColors: Record<string, string> = {
                harmonie: '#22c55e',
                tension: '#ef4444',
                fusion: '#3b82f6',
              };

              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.75rem', background: bgColor, borderRadius: 8,
                  borderLeft: `3px solid ${borderColor}`,
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {asp.planete}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                      {asp.symbole} {asp.aspect} Noeud {asp.noeud}
                      <span style={{ color: natureColors[asp.nature] ?? colors.textMuted }}>
                        {' '}({asp.orbe}°)
                      </span>
                      {asp.exact && (
                        <i className="bi bi-star-fill" style={{ color: '#7c3aed', fontSize: '0.7rem', marginLeft: 4 }} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '1.5rem', background: colors.bgPage, borderRadius: 8, color: colors.textMuted }}>
          <i className="bi bi-dash-circle" style={{ marginRight: 6 }} />
          Aucun aspect majeur aux noeuds lunaires détecté
        </div>
      )}
    </div>
  );
}
